// Top bar above the routed content. Renders:
//   left:  hamburger toggle (mobile only), then page context
//          (parent breadcrumb above, title below if parent present;
//           just title otherwise)
//   right: user avatar menu
//
// Page context reads from PageHeaderContext (set by the routed page
// via useSetPageHeader). When nothing has been registered yet — the
// first render tick before the page's effect lands — we render the
// title slot empty rather than flashing a derived-from-path
// fallback, since titles like "Order RX-…" need real data.

import { Link } from '@tanstack/react-router';
import { usePageHeader } from './PageHeaderContext';
import { UserMenu } from './UserMenu';

type Props = {
  onMenuToggle: () => void;
};

export function TopBar({ onMenuToggle }: Props): JSX.Element {
  const header = usePageHeader();
  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-bg px-4 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onMenuToggle}
          aria-label="Toggle navigation"
          className="-ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-2 hover:bg-bg-soft md:hidden"
        >
          {/* hamburger glyph — three lines via the unicode trigram */}
          <span aria-hidden="true" className="text-lg leading-none">
            ☰
          </span>
        </button>
        <div className="min-w-0">
          {header?.parentLabel ? (
            <p className="truncate text-xs text-ink-2">
              {header.parentTo ? (
                <Link
                  to={header.parentTo as never}
                  className="hover:underline"
                >
                  {header.parentLabel}
                </Link>
              ) : (
                header.parentLabel
              )}
              <span className="px-1">/</span>
            </p>
          ) : null}
          <p className="truncate text-base font-semibold text-ink">
            {header?.title ?? ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <UserMenu />
      </div>
    </header>
  );
}
